import { FLOW_PERMISSION_PROPERTIES } from 'constants/accessConstants';
import {
  ACCESS_HEADER_RESOURCE_ID_KEY,
  ACCESS_HEADER_RESOURCE_KEY,
  API_DOMAIN,
  API_VERSION,
  MFA_TOTP_KEY,
} from 'constants/config';
import { APITags, REQUEST_TYPES } from 'constants/index';
import baseApi from 'services/api';
import {
  ApprovalInitiateReqType,
  ApprovalReqType,
  DeclineReqType,
  GasFeeRequestType,
  GasFeeResponseType,
  PendingPaymentType,
  PendingPayoutTypes,
  PendingRequestType,
  PendingTxnCountResponse,
} from 'types/approval';
import { Map } from 'types/kyb';
import { accessBasedQueryWrapper } from 'utils/access';
import { getFormattedTransaction } from 'utils/approval';

const Approval = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // TODO: refator params support from BE and then use params object here
    getPendingTxnCount: builder.query<PendingTxnCountResponse, void>({
      query: () => ({
        url: `/approval/pending/minified?type=${PendingPayoutTypes.BULK_PAYOUT}&type=${PendingPayoutTypes.SINGLE_PAYOUT}&type=${PendingPayoutTypes.BULK_PAYOUT_V2}`,
      }),
      providesTags: [APITags.GET_PENDING_TXN_COUNT],
      transformResponse: ({ data }) => data,
    }),
    getPendingTxnV2Count: builder.query<PendingTxnCountResponse, void>({
      query: () =>
        accessBasedQueryWrapper(FLOW_PERMISSION_PROPERTIES.VIEW_TRANSACTIONS.permissionId, {
          url: `/banking/merchants/transactions/requests/summary`,
          headers: {
            [ACCESS_HEADER_RESOURCE_KEY]: FLOW_PERMISSION_PROPERTIES.VIEW_TRANSACTIONS.scope,
          },
        }),
      providesTags: [APITags.GET_PENDING_TXN_COUNT],
      transformResponse: ({ data }) => data,
    }),
    // TODO: refator params support from BE and then use params object here
    getPendingTxns: builder.query<PendingPaymentType[], void>({
      query: () => ({
        url: `/approval/pending/detailed?type=${PendingPayoutTypes.BULK_PAYOUT}&type=${PendingPayoutTypes.SINGLE_PAYOUT}&type=${PendingPayoutTypes.BULK_PAYOUT_V2}`,
      }),
      providesTags: [APITags.GET_PENDING_TXNS],
      transformResponse: ({ data }) =>
        data?.approval_requests?.map((item: PendingRequestType) => getFormattedTransaction(item)),
    }),

    requestPayoutApproval: builder.mutation<Map, ApprovalInitiateReqType>({
      query: ({ type, requestDetails, headerKey, accountId }) =>
        accessBasedQueryWrapper(FLOW_PERMISSION_PROPERTIES.APPROVE_PAYMENT.permissionId, {
          url: 'approval/request',
          method: REQUEST_TYPES.POST,
          body: {
            type,
            request_details: requestDetails,
          },
          headers: {
            [MFA_TOTP_KEY]: headerKey,
            [ACCESS_HEADER_RESOURCE_KEY]: FLOW_PERMISSION_PROPERTIES.APPROVE_PAYMENT.scope,
            [ACCESS_HEADER_RESOURCE_ID_KEY]: accountId,
          },
        }),
      invalidatesTags: [APITags.GET_PENDING_TXN_COUNT],
      transformResponse: ({ data }) => data,
    }),

    approvePendingRequests: builder.mutation<Map, ApprovalReqType>({
      query: ({ reqIds, headerKey }) =>
        accessBasedQueryWrapper(FLOW_PERMISSION_PROPERTIES.UPDATE_PAYOUT_REQUESTS.permissionId, {
          url: 'approval/approve',
          method: REQUEST_TYPES.POST,
          body: {
            request_ids: reqIds,
          },
          headers: {
            [MFA_TOTP_KEY]: headerKey,
            [ACCESS_HEADER_RESOURCE_KEY]: FLOW_PERMISSION_PROPERTIES.UPDATE_PAYOUT_REQUESTS.scope,
          },
        }),
      transformResponse: ({ data }) => data,
      invalidatesTags: [APITags.GET_PENDING_TXN_COUNT],
    }),

    declinePendingRequests: builder.mutation<Map, DeclineReqType>({
      query: ({ reqIds }) =>
        accessBasedQueryWrapper(FLOW_PERMISSION_PROPERTIES.UPDATE_PAYOUT_REQUESTS.permissionId, {
          url: 'approval/decline',
          method: REQUEST_TYPES.POST,
          body: {
            request_ids: reqIds,
          },
          headers: {
            [ACCESS_HEADER_RESOURCE_KEY]: FLOW_PERMISSION_PROPERTIES.UPDATE_PAYOUT_REQUESTS.scope,
          },
        }),
      transformResponse: ({ data }) => data,
      invalidatesTags: [APITags.GET_PENDING_TXN_COUNT, APITags.GET_PENDING_TXNS],
    }),

    getGasFee: builder.query<GasFeeResponseType, GasFeeRequestType>({
      query: ({ txnId }) => ({
        url: `${API_DOMAIN}/b2b-transfer/${API_VERSION}/bulk-payout/quote/${txnId}`,
      }),
      transformResponse: ({ data }) => data,
    }),
    getGasFeeV2: builder.query<GasFeeResponseType, GasFeeRequestType>({
      query: ({ txnId }) => ({
        url: `${API_DOMAIN}/b2b-transfer/v2/bulk-payout/quote/${txnId}`,
      }),
      transformResponse: ({ data }) => data,
    }),
  }),
});

export const {
  useGetPendingTxnV2CountQuery,
  useGetPendingTxnCountQuery,
  useLazyGetPendingTxnCountQuery,
  useGetPendingTxnsQuery,
  useRequestPayoutApprovalMutation,
  useApprovePendingRequestsMutation,
  useDeclinePendingRequestsMutation,
  useGetGasFeeV2Query,
  useLazyGetGasFeeQuery,
  useLazyGetGasFeeV2Query,
} = Approval;
